import { useState } from 'react';

import Icon from './Icon.jsx';

/**
 * Confirming an irreversible deletion.
 *
 * Two things are being established, and they are not the same thing. That the
 * person at the keyboard is the account holder — a borrowed laptop with a live
 * session should not be able to destroy someone's account — and that they mean
 * it, rather than having clicked the wrong item in a menu where "Sign out" sits
 * two rows above.
 *
 * A password does both at once. Accounts created through Google have no
 * password to give, so those confirm by typing their own email address: it
 * proves nothing about identity that the session cookie did not already prove,
 * but it is deliberate in a way that clicking is not, and identity is not the
 * part at risk for an account reachable only through Google in the first place.
 *
 * The consequences are listed above the field rather than below it. Someone
 * reading downwards should know what they are agreeing to before they are asked
 * to type anything.
 */
export default function DeleteAccountModal({ account, onClose }) {
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  // No password on the account means it arrived through Google.
  const byPassword = account.user?.hasPassword !== false;
  const email = account.user?.email ?? '';

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !confirmation.trim()) return;
    setBusy(true);
    setProblem(null);
    try {
      await account.deleteAccount(confirmation);
      // Nothing to close — the account is gone and the app re-renders signed out.
    } catch (error) {
      setProblem(error.message ?? 'Could not delete the account.');
      setBusy(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-title">
      <button className="modal__scrim" onClick={onClose} aria-label="Close" tabIndex={-1} />

      <div className="modal__sheet modal__sheet--account">
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={14} />
        </button>

        <h2 id="delete-title" className="modal__title">
          Delete your account
        </h2>
        <p className="modal__sub">This cannot be undone.</p>

        <ul className="delete-list">
          <li>Your streak, saved verses and everything you have read</li>
          <li>Your email address and password</li>
          <li>Every device you are signed in on</li>
        </ul>

        {account.user?.premium ? (
          <p className="modal__notice">
            Your subscription is cancelled straight away, so you are not charged
            again. The rest of the period you have already paid for is not
            refunded automatically — ask before deleting if you want it back.
          </p>
        ) : null}

        <form className="account-form" onSubmit={submit}>
          <label className="field">
            {/* data-clarity-mask: the Google-sign-in branch prints the
                customer's own email address as the thing to type, which session
                replay would otherwise record verbatim. */}
            <span data-clarity-mask="true">
              {byPassword ? 'Your password' : `Type ${email} to confirm`}
            </span>
            <input
              type={byPassword ? 'password' : 'email'}
              autoComplete={byPassword ? 'current-password' : 'off'}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={busy}
              required
              // Deliberately not autofocused: the list above is the part worth
              // reading, and a focused field raises a phone keyboard over it.
            />
          </label>

          {problem ? <p className="account-form__problem">{problem}</p> : null}

          <button
            type="submit"
            className="btn btn--danger btn--block btn--lg"
            disabled={busy || !confirmation.trim()}
          >
            {busy ? 'Deleting…' : 'Delete everything'}
          </button>

          <button type="button" className="btn btn--ghost btn--block" onClick={onClose} disabled={busy}>
            Keep my account
          </button>
        </form>
      </div>
    </div>
  );
}
